import { useEffect, useRef, useState } from "react";
import { ChevronDownIcon } from "lucide-react";

interface LabeledSelectProps {
  label: string;
  labelColor?: string;
  value: string | null;
  options: { label: string; value: string | number }[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

const LabeledSelect = ({
  label,
  labelColor = "#0E59C6",
  value,
  options,
  onChange,
  placeholder = "선택하세요",
  disabled = false,
}: LabeledSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedLabel = options.find(
    (opt) => String(opt.value) === value
  )?.label;

  return (
    <div className="relative w-full" ref={containerRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`text-left font-pretendard border rounded-[5px] w-full border-[#D6D6DE] text-[15px] relative flex items-center justify-between px-[15px] h-[36px] ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        <div className="flex items-center justify-start flex-1 min-w-0">
          <span
            className="font-pretendard font-semibold mr-1 flex-shrink-0 text-[15px] pointer-events-none"
            style={{ color: labelColor }}
          >
            {label} |
          </span>
          <span
            className={`text-[15px] font-pretendard flex-1 truncate ${
              !selectedLabel ? "text-[#939399]" : "text-[#333333]"
            }`}
          >
            {selectedLabel || placeholder}
          </span>
        </div>
        {/* 화살표 아이콘 - SideMultiSelect와 동일한 스타일 */}
        <ChevronDownIcon className="size-4 opacity-50 pointer-events-none flex-shrink-0" />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 w-full mt-1 bg-white border border-[#D6D6DE] rounded-[5px] shadow-lg z-50 max-h-[200px] overflow-y-auto">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`w-full text-left px-3 py-2 text-[15px] hover:bg-[#F5F5F5] font-pretendard ${
                String(option.value) === value
                  ? "bg-[#F0F7FF] text-[#0E59C6] font-medium"
                  : "text-[#333333]"
              }`}
              onClick={() => {
                onChange(String(option.value));
                setIsOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LabeledSelect;
